import React from 'react';

interface HeroProps {
  onCtaClick: () => void;
}

const Hero: React.FC<HeroProps> = ({ onCtaClick }) => {
  return (
    <div className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-[#FAF7F2]">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[10s] hover:scale-105"
          style={{ backgroundImage: "url('https://i.imgur.com/TljgxFC.jpeg')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#FAF7F2]/95 via-[#FAF7F2]/80 to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-[#FAF7F2] via-transparent to-transparent opacity-60"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        {/* Left: Text Content */}
        <div className="space-y-8 animate-fade-in-up">
          <div className="inline-flex items-center px-4 py-1.5 bg-white/60 backdrop-blur-md border border-pink-200 rounded-full text-pink-400 font-bold text-sm tracking-widest uppercase shadow-sm">
            <span className="w-2 h-2 bg-pink-300 rounded-full mr-2 animate-pulse"></span>
            30 Sessions Intensive Coaching
          </div>
          
          <h1 className="text-5xl md:text-7xl font-bold text-slate-800 leading-tight tracking-tight">
            30회 완성, <br/><span className="text-pink-300 italic">토플 80+</span>
          </h1>

          <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl font-medium">
            "단 30회 밀착 코칭으로 대학 수준의 진짜 영어 실력을 완성합니다." TOEFL 공식 자료와 1:1 AI 밀착 코치로 4대 영역을 한 번에 정복하세요.
          </p>
          
          <div className="pt-4">
            <button 
              onClick={onCtaClick}
              className="bg-pink-300 hover:bg-pink-400 shadow-[0_10px_25px_-5px_rgba(244,114,182,0.2)] text-white px-10 py-5 rounded-xl font-black text-lg transition-all active:scale-95 group flex items-center"
            >
              무료 레벨 테스트 신청하기
              <svg className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Right: AI Coaching Interface Visualization */}
        <div className="relative hidden lg:block animate-fade-in-up animate-delay-200">
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 overflow-hidden transform hover:-translate-y-1 transition-transform duration-500">
             <div className="bg-[#FAF7F2]/50 border-b p-4 flex items-center justify-between">
                <div className="flex space-x-1.5">
                  <div className="w-3 h-3 bg-pink-50 rounded-full"></div>
                  <div className="w-3 h-3 bg-pink-100 rounded-full"></div>
                  <div className="w-3 h-3 bg-pink-200 rounded-full"></div>
                </div>
                <span className="text-[10px] font-bold text-pink-300 tracking-tighter uppercase">TOEFL LIVE AI COACHING v2.5</span>
             </div>
             
             <div className="flex h-[450px]">
                <div className="w-1/3 bg-[#FAF7F2]/30 border-r flex flex-col items-center justify-center p-6">
                   <div className="relative">
                      <div className="w-28 h-28 rounded-full border-4 border-pink-50 overflow-hidden bg-white mb-4 shadow-sm">
                        <img src="https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=200" alt="AI Coach" className="w-full h-full object-cover" />
                      </div>
                      <div className="absolute bottom-5 right-1 w-6 h-6 bg-pink-300 border-4 border-white rounded-full"></div>
                   </div>
                   <div className="text-center">
                      <h4 className="font-bold text-slate-700">Sarah (AI Coach)</h4>
                      <div className="flex items-center justify-center mt-2 space-x-1">
                         {[...Array(4)].map((_, i) => (
                           <div key={i} className={`w-1 bg-pink-400 rounded-full animate-bounce`} style={{height: `${10 + Math.random()*20}px`, animationDelay: `${i*0.1}s`}}></div>
                         ))}
                      </div>
                   </div>
                </div>

                <div className="w-2/3 p-8 bg-white/60 font-serif overflow-y-auto">
                   <div className="mb-6 flex justify-between items-end border-b border-[#FAF7F2] pb-2">
                      <span className="text-[11px] font-bold text-slate-300 uppercase tracking-widest">Essay Correction</span>
                      <span className="text-[11px] font-bold text-pink-300">LIVE FEEDBACK</span>
                   </div>
                   <div className="text-slate-600 leading-relaxed text-sm space-y-4">
                      <p>Many students today believe that <span className="bg-pink-50 border-b-2 border-pink-100 px-1 text-slate-800">digital learning</span> is more effective than traditional methods.</p>
                      <p>However, I <span className="bg-red-50 text-red-400 border-b-2 border-red-100 px-1">strongly thinks</span> that physical classrooms provide essential social interactions...</p>
                   </div>
                   
                   <div className="mt-8 space-y-3">
                      <div className="p-3 bg-pink-50/50 border-l-4 border-pink-200 rounded-r-lg backdrop-blur-sm">
                         <p className="text-[11px] font-bold text-pink-300 mb-1">Grammar Suggestion</p>
                         <p className="text-[12px] text-slate-500">Change "strongly thinks" to <span className="text-pink-400 font-bold">"strongly think"</span>.</p>
                      </div>
                   </div>
                </div>
             </div>
          </div>

          <div className="absolute -bottom-10 -right-8 w-80 bg-white/90 backdrop-blur-xl rounded-2xl shadow-2xl p-6 border border-white/50 transform rotate-1 hover:rotate-0 transition-all duration-300">
             <h5 className="text-xs font-bold text-slate-700 mb-4 flex justify-between">
                <span>Speaking Achievement</span>
                <span className="text-pink-300 font-black">Lv.8</span>
             </h5>
             <div className="space-y-4">
                <div className="flex items-center space-x-3">
                   <div className="flex-grow h-2 bg-[#FAF7F2] rounded-full overflow-hidden">
                      <div className="h-full bg-pink-300 rounded-full" style={{width: '85%'}}></div>
                   </div>
                   <span className="text-[10px] font-bold text-slate-300 min-w-[30px]">85%</span>
                </div>
             </div>
             <div className="mt-4 pt-4 border-t border-[#FAF7F2] flex justify-center space-x-8">
                <div className="text-center">
                   <div className="text-lg font-black text-slate-800">82</div>
                   <div className="text-[9px] font-bold text-slate-300 uppercase">Fluency</div>
                </div>
                <div className="text-center">
                   <div className="text-lg font-black text-slate-800">89</div>
                   <div className="text-[9px] font-bold text-slate-300 uppercase">Context</div>
                </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;